import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Grid,
  InputAdornment,
  Chip,
  Alert,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  LinearProgress,
  Tabs,
  Tab,
  Badge,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Inventory as StockIcon,
  Store as StoreIcon,
  TrendingUp as HighIcon,
  TrendingDown as LowIcon,
  Warning as WarningIcon,
  CheckCircle as OkIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  SwapHoriz as TransferIcon,
} from "@mui/icons-material";

const CentralizedStock = () => {
  const [activeTab, setActiveTab] = useState(0);

  // Sample stores
  const stores = [
    { code: "HQ001", name: "HQ - Mumbai", type: "HQ" },
    { code: "WH001", name: "Warehouse - Navi Mumbai", type: "Warehouse" },
    { code: "RT001", name: "Retail - Andheri", type: "Retail" },
    { code: "RT002", name: "Retail - Pune", type: "Retail" },
    { code: "RT003", name: "Retail - Thane", type: "Retail" },
  ];

  // Sample centralized stock data
  const [stockData, setStockData] = useState([
    {
      id: 1,
      productCode: "PROD001",
      productName: "Samsung Galaxy S23",
      category: "Electronics",
      HQ001: 150,
      WH001: 500,
      RT001: 45,
      RT002: 38,
      RT003: 25,
      totalStock: 758,
      minStock: 200,
      reorderLevel: 300,
    },
    {
      id: 2,
      productCode: "PROD002",
      productName: "Dell Laptop i5",
      category: "Electronics",
      HQ001: 80,
      WH001: 300,
      RT001: 22,
      RT002: 18,
      RT003: 15,
      totalStock: 435,
      minStock: 150,
      reorderLevel: 250,
    },
    {
      id: 3,
      productCode: "PROD003",
      productName: "Nike Running Shoes",
      category: "Footwear",
      HQ001: 200,
      WH001: 800,
      RT001: 65,
      RT002: 55,
      RT003: 45,
      totalStock: 1165,
      minStock: 300,
      reorderLevel: 500,
    },
    {
      id: 4,
      productCode: "PROD004",
      productName: "Adidas T-Shirt",
      category: "Apparel",
      HQ001: 300,
      WH001: 1200,
      RT001: 85,
      RT002: 70,
      RT003: 55,
      totalStock: 1710,
      minStock: 400,
      reorderLevel: 600,
    },
    {
      id: 5,
      productCode: "PROD005",
      productName: "Sony Headphones",
      category: "Electronics",
      HQ001: 50,
      WH001: 150,
      RT001: 12,
      RT002: 8,
      RT003: 5,
      totalStock: 225,
      minStock: 100,
      reorderLevel: 180,
    },
    {
      id: 6,
      productCode: "PROD006",
      productName: "iPhone 15 Pro",
      category: "Electronics",
      HQ001: 100,
      WH001: 250,
      RT001: 28,
      RT002: 22,
      RT003: 18,
      totalStock: 418,
      minStock: 150,
      reorderLevel: 250,
    },
    {
      id: 7,
      productCode: "PROD007",
      productName: "Puma Sneakers",
      category: "Footwear",
      HQ001: 120,
      WH001: 400,
      RT001: 38,
      RT002: 32,
      RT003: 25,
      totalStock: 615,
      minStock: 200,
      reorderLevel: 350,
    },
    {
      id: 8,
      productCode: "PROD008",
      productName: "Levi's Jeans",
      category: "Apparel",
      HQ001: 180,
      WH001: 650,
      RT001: 52,
      RT002: 45,
      RT003: 38,
      totalStock: 965,
      minStock: 250,
      reorderLevel: 400,
    },
  ]);

  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [storeFilter, setStoreFilter] = useState("all");
  const [stockLevelFilter, setStockLevelFilter] = useState("all");

  // Helper functions
  const getStockStatus = (totalStock, minStock, reorderLevel) => {
    if (totalStock <= minStock) return { label: "Critical", color: "error", icon: <WarningIcon /> };
    if (totalStock <= reorderLevel) return { label: "Low", color: "warning", icon: <LowIcon /> };
    return { label: "Good", color: "success", icon: <OkIcon /> };
  };

  const getStockPercentage = (totalStock, reorderLevel) => {
    return Math.min((totalStock / (reorderLevel * 2)) * 100, 100);
  };

  const getFilteredStock = () => {
    let filtered = stockData;

    if (searchQuery) {
      filtered = filtered.filter((item) =>
        Object.values(item).some((value) =>
          String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    }

    if (categoryFilter !== "all") {
      filtered = filtered.filter((item) => item.category === categoryFilter);
    }

    if (stockLevelFilter === "critical") {
      filtered = filtered.filter((item) => item.totalStock <= item.minStock);
    } else if (stockLevelFilter === "low") {
      filtered = filtered.filter((item) => item.totalStock > item.minStock && item.totalStock <= item.reorderLevel);
    } else if (stockLevelFilter === "good") {
      filtered = filtered.filter((item) => item.totalStock > item.reorderLevel);
    }

    if (activeTab === 1) {
      filtered = filtered.filter((item) => item.category === "Electronics");
    } else if (activeTab === 2) {
      filtered = filtered.filter((item) => item.category === "Footwear");
    } else if (activeTab === 3) {
      filtered = filtered.filter((item) => item.category === "Apparel");
    }

    return filtered;
  };

  const filteredStock = getFilteredStock();

  // Calculate statistics
  const totalProducts = stockData.length;
  const totalStockAllStores = stockData.reduce((sum, item) => sum + item.totalStock, 0);
  const criticalStock = stockData.filter(item => item.totalStock <= item.minStock).length;
  const lowStock = stockData.filter(item => item.totalStock > item.minStock && item.totalStock <= item.reorderLevel).length;
  const goodStock = stockData.filter(item => item.totalStock > item.reorderLevel).length;

  // Categories
  const categories = ["Electronics", "Footwear", "Apparel"];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <StockIcon sx={{ color: 'primary.main', fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight="bold" color="primary">
              Centralized Stock Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View and manage stock across all stores • {filteredStock.length} products
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<TransferIcon />}
            sx={{ borderRadius: 2 }}
          >
            Inter-Store Transfer
          </Button>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            sx={{ borderRadius: 2 }}
          >
            Export Report
          </Button>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #1976d2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Products</Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary.main">
                    {totalProducts}
                  </Typography>
                </Box>
                <StockIcon sx={{ fontSize: 48, color: 'primary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #9c27b0' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Stock</Typography>
                  <Typography variant="h5" fontWeight="bold" color="secondary.main">
                    {totalStockAllStores.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">units</Typography>
                </Box>
                <HighIcon sx={{ fontSize: 48, color: 'secondary.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #d32f2f' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Critical Stock</Typography>
                  <Typography variant="h4" fontWeight="bold" color="error.main">
                    {criticalStock}
                  </Typography>
                </Box>
                <WarningIcon sx={{ fontSize: 48, color: 'error.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #ff9800' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Low Stock</Typography>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {lowStock}
                  </Typography>
                </Box>
                <LowIcon sx={{ fontSize: 48, color: 'warning.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderLeft: '4px solid #2e7d32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Good Stock</Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {goodStock}
                  </Typography>
                </Box>
                <OkIcon sx={{ fontSize: 48, color: 'success.main', opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab 
            label={<Badge badgeContent={totalProducts} color="primary">All Products</Badge>} 
            icon={<StockIcon />} 
            iconPosition="start" 
          />
          <Tab 
            label={<Badge badgeContent={stockData.filter(s => s.category === "Electronics").length} color="secondary">Electronics</Badge>} 
            iconPosition="start" 
          />
          <Tab 
            label={<Badge badgeContent={stockData.filter(s => s.category === "Footwear").length} color="info">Footwear</Badge>} 
            iconPosition="start" 
          />
          <Tab 
            label={<Badge badgeContent={stockData.filter(s => s.category === "Apparel").length} color="success">Apparel</Badge>} 
            iconPosition="start" 
          />
        </Tabs>
      </Paper>

      {/* Search and Filter Section */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Search Products..."
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={2.5}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                label="Category"
              >
                <MenuItem value="all">All Categories</MenuItem>
                {categories.map(cat => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2.5}>
            <FormControl fullWidth>
              <InputLabel>Stock Level</InputLabel>
              <Select
                value={stockLevelFilter}
                onChange={(e) => setStockLevelFilter(e.target.value)}
                label="Stock Level"
              >
                <MenuItem value="all">All Levels</MenuItem>
                <MenuItem value="critical">Critical</MenuItem>
                <MenuItem value="low">Low</MenuItem>
                <MenuItem value="good">Good</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setSearchQuery("");
                  setCategoryFilter("all");
                  setStockLevelFilter("all");
                }}
                startIcon={<RefreshIcon />}
              >
                Reset
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Table Section - Scrollable */}
      <Paper sx={{ borderRadius: 2, boxShadow: 3, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa', minWidth: 120 }}>Product Code</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa', minWidth: 200 }}>Product Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa', minWidth: 100 }}>Category</TableCell>
                {stores.map(store => (
                  <TableCell key={store.code} sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa', minWidth: 100 }} align="center">
                    {store.code}
                    <Typography variant="caption" display="block" color="text.secondary">
                      {store.type}
                    </Typography>
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa', minWidth: 120 }} align="center">
                  Total Stock
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa', minWidth: 120 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f8f9fa', minWidth: 80 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredStock.length > 0 ? (
                filteredStock.map((item) => {
                  const status = getStockStatus(item.totalStock, item.minStock, item.reorderLevel);
                  const percentage = getStockPercentage(item.totalStock, item.reorderLevel);
                  return (
                    <TableRow key={item.id} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="700" sx={{ fontFamily: 'monospace' }}>
                          {item.productCode}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600">{item.productName}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={item.category} size="small" variant="outlined" />
                      </TableCell>
                      {stores.map(store => (
                        <TableCell key={store.code} align="center">
                          <Typography variant="body2" fontWeight="600">
                            {item[store.code] || 0}
                          </Typography>
                        </TableCell>
                      ))}
                      <TableCell align="center">
                        <Box>
                          <Typography variant="h6" fontWeight="bold" color="primary">
                            {item.totalStock.toLocaleString()}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={percentage}
                            sx={{
                              height: 6,
                              borderRadius: 3,
                              backgroundColor: '#e0e0e0',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: status.color === 'error' ? '#d32f2f' : 
                                                 status.color === 'warning' ? '#ff9800' : '#2e7d32',
                              },
                            }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={status.icon}
                          label={status.label}
                          size="small"
                          color={status.color}
                          sx={{ fontWeight: 600 }}
                        />
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                          Min: {item.minStock}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Details">
                          <IconButton size="small" sx={{ color: 'info.main' }}>
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <StockIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">No products found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Stock Level Legend */}
      <Paper sx={{ p: 2, mt: 3, borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Stock Level Legend:</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip icon={<WarningIcon />} label="Critical" size="small" color="error" />
              <Typography variant="caption" color="text.secondary">
                Stock ≤ Minimum Stock Level
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip icon={<LowIcon />} label="Low" size="small" color="warning" />
              <Typography variant="caption" color="text.secondary">
                Stock ≤ Reorder Level
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip icon={<OkIcon />} label="Good" size="small" color="success" />
              <Typography variant="caption" color="text.secondary">
                Stock {'>'} Reorder Level
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
};

export default CentralizedStock;

